import { css } from 'styled-components';

/**
 * Shared visual base for aspect-ratio ("dimension") badges — the small pills
 * that show a clip's aspect ratio (e.g. "16:9"). Consumers compose this into
 * their own styled component, take the publish-state colors from
 * `dimensionBadgeStateColors` below, and layer on the divergent bits (display,
 * transitions, hover / download behavior).
 */
export const dimensionBadgeBase = css`
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  font-size: 10px;
`;

/**
 * The publish state an aspect-ratio badge is in, as colors. Shared because two
 * surfaces show the same three states for the same clip variant — Content
 * Studio's clip card (`VideoClipCard`) and the Story Manager clip row
 * (`StoryClipRow`) — so a variant must not read as published on one and draft
 * on the other.
 *
 * Filled green = published, outlined = draft, gold = a render in flight. Gold
 * is the color `publishStatusUi` already gives a queued/rendering
 * `PublishRecord`, so an in-flight variant reads the same on the badge as it
 * does in the publish progress drawer.
 */
export const dimensionBadgeStateColors = ({
  publishing,
  inactive,
}: {
  publishing?: boolean;
  inactive?: boolean;
}) =>
  publishing
    ? css`
        border-color: var(--accent);
        color: var(--accent);
      `
    : inactive
      ? css`
          border-color: var(--border);
          color: var(--text-muted);
        `
      : css`
          border-color: var(--primary);
          color: var(--background);
          background-color: var(--primary);
          font-weight: 600;
        `;
